import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Dashboard,
    People,
    Payment,
    Link as LinkIcon,
    Assessment,
    AccountCircle,
    Logout,
    Close,
    Business,
    ChevronRight,
    ChevronLeft,
    SwapHoriz,
    Person
    // Receipt, Inventory : menus Factures / Articles masqués
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { hosts } from '../../config/environment';
import useSidebarCollapsed from '../../hooks/useSidebarCollapsed';
import { getOrganizationRoleKeys, normalizeRoleKey } from '../../utils/organizationRoleUtils';

// Ordre: Tableau de bord, Demandes, Liens, Factures, Articles, Transactions, Clients, Rapports, Profil
const ALL_MENU_ITEMS = [
    { id: 'dashboard', label: 'Tableau de bord', icon: Dashboard, path: '/cashier', color: 'amber' },
    { id: 'payment-requests', label: 'Demandes de paiement', icon: Payment, path: '/cashier/payment-requests', color: 'emerald' },
    { id: 'payment-links', label: 'Liens de paiement', icon: LinkIcon, path: '/cashier/payment-links', color: 'blue' },
    // { id: 'invoices', label: 'Factures', icon: Receipt, path: '/cashier/invoices', color: 'orange' }, // Factures / Articles masqués pour la mise en dev : décommenter pour réactiver
    // { id: 'articles', label: 'Articles', icon: Inventory, path: '/cashier/articles', color: 'teal' }, // Factures / Articles masqués pour la mise en dev : décommenter pour réactiver
    { id: 'transactions', label: 'Transactions', icon: SwapHoriz, path: '/cashier/transactions', color: 'indigo' },
    { id: 'customers', label: 'Clients', icon: People, path: '/cashier/customers', color: 'teal' },
    { id: 'reports', label: 'Rapports', icon: Assessment, path: '/cashier/reports', color: 'orange' },
    { id: 'profile', label: 'Profil', icon: Person, path: '/cashier/profile', color: 'sky' }
];

const MENU_ITEMS = ALL_MENU_ITEMS;

const colorClasses = {
    amber: {
        active: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/25 border-amber-500/30',
        icon: 'text-amber-600',
        hover: 'hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200'
    },
    emerald: {
        active: 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/25 border-emerald-500/30',
        icon: 'text-emerald-600',
        hover: 'hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200'
    },
    blue: {
        active: 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/25 border-blue-500/30',
        icon: 'text-blue-600',
        hover: 'hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200'
    },
    teal: {
        active: 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/25 border-teal-500/30',
        icon: 'text-teal-600',
        hover: 'hover:bg-teal-50 hover:text-teal-700 hover:border-teal-200'
    },
    orange: {
        active: 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/25 border-orange-500/30',
        icon: 'text-orange-600',
        hover: 'hover:bg-orange-50 hover:text-orange-700 hover:border-orange-200'
    },
    indigo: {
        active: 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-lg shadow-indigo-500/25 border-indigo-500/30',
        icon: 'text-indigo-600',
        hover: 'hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200'
    },
    sky: {
        active: 'bg-gradient-to-r from-sky-500 to-cyan-500 text-white shadow-lg shadow-sky-500/25 border-sky-500/30',
        icon: 'text-sky-600',
        hover: 'hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200'
    }
};

const CashierSidebar = ({ isOpen, onToggle }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, selectedOrganization, organizations, selectOrganization, logout } = useAuth();
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [collapsed, toggleCollapsed] = useSidebarCollapsed('flow_cashier_sidebar_collapsed');

    // Les pages caissier décalent leur contenu avec lg:ml-[var(--cashier-sidebar-w)].
    useEffect(() => {
        document.documentElement.style.setProperty('--cashier-sidebar-w', collapsed ? '5rem' : '18rem');
    }, [collapsed]);

    const isActive = (path) => {
        if (path === '/cashier') return location.pathname === '/cashier';
        return location.pathname.startsWith(path);
    };

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const getLogoUrl = () => {
        if (!selectedOrganization?.id) return '';
        return `${hosts.api}/exposed/v1/mda/images/show?target=logo&target_id=${selectedOrganization.id}`;
    };

    const selectedRoleKey = normalizeRoleKey(selectedOrganization?.selectedRole || selectedOrganization?.role);
    const roleKeys = getOrganizationRoleKeys(selectedOrganization);
    const canSwitchToAdmin =
        selectedOrganization?.id &&
        selectedRoleKey === 'cashier' &&
        roleKeys.includes('admin');

    const handleSwitchToAdmin = () => {
        if (!selectedOrganization?.id) return;
        const org =
            organizations?.find((o) => o.id === selectedOrganization.id) || selectedOrganization;
        selectOrganization(selectedOrganization.id, 'admin', org);
        setShowProfileMenu(false);
        if (window.innerWidth < 1024) onToggle();
        // L'interface admin utilise une structure différente; recharger évite les états résiduels.
        window.location.reload();
    };

    return (
        <>
            {isOpen && (
                <div
                    className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 lg:hidden"
                    onClick={onToggle}
                    aria-hidden="true"
                />
            )}

            <aside
                className={`fixed top-0 left-0 h-full w-72 ${collapsed ? 'lg:w-20' : 'lg:w-72'} z-50 transform transition-[width,transform] duration-300 ease-out lg:translate-x-0 ${
                    isOpen ? 'translate-x-0' : '-translate-x-full'
                } flex flex-col bg-white border-r border-slate-200 shadow-xl`}
            >
                {/* Brand */}
                <div className={`flex items-center justify-between p-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-amber-50/40 ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}>
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/25 ring-2 ring-amber-500/20">
                            <Dashboard className="text-white text-xl" />
                        </div>
                        <div className={collapsed ? 'lg:hidden' : ''}>
                            <h2 className="text-base font-bold text-slate-800 tracking-tight">FLOW</h2>
                            <p className="text-xs text-slate-500 font-medium">Caissier</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onToggle}
                        className="lg:hidden p-2.5 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors"
                    >
                        <Close className="text-xl" />
                    </button>
                </div>

                {/* Organisation */}
                {selectedOrganization && (
                    <div className={`p-4 border-b border-slate-100 bg-slate-50/50 ${collapsed ? 'lg:px-0 lg:py-3' : ''}`} title={collapsed ? selectedOrganization.name : undefined}>
                        <div className={`flex items-center gap-3 ${collapsed ? 'lg:justify-center' : ''}`}>
                            <div className="w-12 h-12 rounded-xl overflow-hidden bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 shadow-sm">
                                <img
                                    src={getLogoUrl()}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                                {!getLogoUrl() && <Business className="text-slate-400 text-2xl" />}
                            </div>
                            <div className={`flex-1 min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
                                <p className="text-sm font-semibold text-slate-800 truncate">
                                    {selectedOrganization.name}
                                </p>
                                <p className="text-xs text-slate-500">Organisation</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Menu */}
                <nav className={`flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-1 ${collapsed ? 'lg:px-3' : ''}`}>
                    {MENU_ITEMS.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path);
                        const colors = colorClasses[item.color] || colorClasses.amber;
                        return (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => {
                                    navigate(item.path);
                                    if (window.innerWidth < 1024) onToggle();
                                }}
                                title={collapsed ? item.label : undefined}
                                className={`group relative w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 border ${
                                    collapsed ? 'lg:justify-center lg:px-0' : ''
                                } ${
                                    active ? colors.active : `text-slate-600 border-transparent ${colors.hover}`
                                }`}
                            >
                                <Icon
                                    className={`flex-shrink-0 text-xl ${active ? 'text-white' : colors.icon}`}
                                />
                                <span className={`font-medium text-sm flex-1 truncate ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                                {active && <ChevronRight className={`text-white/90 text-lg flex-shrink-0 ${collapsed ? 'lg:hidden' : ''}`} />}
                            </button>
                        );
                    })}
                </nav>

                {/* Profil */}
                <div className={`p-4 border-t border-slate-100 bg-slate-50/50 space-y-2 ${collapsed ? 'lg:px-3' : ''}`}>
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setShowProfileMenu(!showProfileMenu)}
                            title={collapsed ? (user?.name || user?.username || 'Utilisateur') : undefined}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white border border-slate-200 transition-colors text-left shadow-sm ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}
                        >
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-md">
                                <AccountCircle className="text-white text-xl" />
                            </div>
                            <div className={`flex-1 min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
                                <p className="text-sm font-semibold text-slate-800 truncate">
                                    {user?.name || user?.username || 'Utilisateur'}
                                </p>
                                <p className="text-xs text-slate-500 truncate">{user?.email || ''}</p>
                            </div>
                        </button>

                        {showProfileMenu && (
                            <div className={`absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden ${collapsed ? 'lg:right-auto lg:w-56' : ''}`}>
                                {canSwitchToAdmin && (
                                    <button
                                        type="button"
                                        onClick={handleSwitchToAdmin}
                                        className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-50 transition-colors text-left"
                                    >
                                        <Dashboard className="text-slate-400 text-xl" />
                                        <span className="font-medium">Basculer en Admin</span>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleLogout}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-red-50 hover:text-red-600 transition-colors text-left"
                                >
                                    <Logout className="text-slate-400 text-xl" />
                                    <span className="font-medium">Déconnexion</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Réduire / agrandir (grand écran) */}
                    <button
                        type="button"
                        onClick={toggleCollapsed}
                        title={collapsed ? 'Agrandir le menu' : 'Réduire le menu'}
                        className={`hidden lg:flex w-full items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-500 hover:bg-white hover:text-slate-800 transition-colors ${collapsed ? 'justify-center px-0' : ''}`}
                    >
                        <ChevronLeft className={`transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} fontSize="small" />
                        {!collapsed && <span>Réduire le menu</span>}
                    </button>
                </div>
            </aside>
        </>
    );
};

export default CashierSidebar;
